export type Theme = "dark" | "light";

const THEME_STORAGE_KEY = "theme";
const DEFAULT_THEME: Theme = "dark";

/**
 * Get the stored theme from localStorage
 */
export const getStoredTheme = (): Theme => {
	if (typeof window === "undefined") return DEFAULT_THEME;

	const stored = localStorage.getItem(THEME_STORAGE_KEY);
	if (stored === "dark" || stored === "light") {
		return stored;
	}

	return DEFAULT_THEME;
};

/**
 * Save theme to localStorage
 */
export const setStoredTheme = (theme: Theme): void => {
	if (typeof window === "undefined") return;
	localStorage.setItem(THEME_STORAGE_KEY, theme);
};

/**
 * Apply theme classes to document
 */
export const applyThemeToDocument = (theme: Theme): void => {
	if (typeof window === "undefined") return;

	const root = document.documentElement;
	const body = document.body;

	// Remove both classes first
	root.classList.remove("dark", "light");
	body.classList.remove("dark", "light");

	// Add the current theme class
	root.classList.add(theme);
	body.classList.add(theme);
};

/**
 * Apply theme immediately (for blocking script to prevent flash)
 * This should be called before React renders
 */
if (typeof window !== "undefined") {
	const initialTheme = getStoredTheme();
	applyThemeToDocument(initialTheme);
}

/**
 * Get system preference for theme
 */
export const getSystemTheme = (): Theme => {
	if (typeof window === "undefined") return DEFAULT_THEME;

	return window.matchMedia("(prefers-color-scheme: dark)").matches
		? "dark"
		: "light";
};
